import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BackButton } from "../../../components/common/GeneralComponent";
import Loader from "../../../components/partials/Loader";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import AdministrativeInfo from "./AdministrativeInfo";
import BeneficiaryDetails from "./BeneficiaryDetails";
import MicroGrantDetails from "./MicroGrantDetails";
import VerificationSupport from "./VerificationSupport";
import OverallProgress from "./OverallProgress";
import Attachments from "./Attachments";

const EditMicroGrant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [loader, setLoader] = useState(true);
  const [isLeftClicked, setIsLeftClicked] = useState(false);
  const [isRightClicked, setIsRightClicked] = useState(false);
  const [grantData, setGrantData] = useState(null);
  const [activeTab, setActiveTab] = useState("");

  const allTabs = [
    {
      name: "Administrative Info",
      permission: "micro-grant-administrative-info-update",
    },
    {
      name: "Beneficiary Details",
      permission: "micro-grant-beneficiary-details-update",
    },
    {
      name: "Micro Grant Details",
      permission: "micro-grant-grants-details-update",
    },
    {
      name: "Verification & Support",
      permission: "micro-grant-verification-support-update",
    },
    {
      name: "Overall Progress",
      permission: "micro-grant-overall-progress-update",
    },
    { name: "Attachments", permission: "micro-grant-attachment-update" },
  ];
  const allowedTabs = useMemo(
    () => allTabs.filter((tab) => permissions.includes(tab.permission)),
    [permissions]
  );
  const fetchGrantData = async () => {
    try {
      setLoader(true);
      const res = await fetchData(
        `micro-grants/${id}`,
        "GET",
        null,
        {},
        {},
        navigate
      );
      if (res?.data) {
        setGrantData(res.data);
      }
    } catch (error) {
      console.error("Error fetching grant data:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchGrantData();
  }, [id]);

  useEffect(() => {
    if (allowedTabs.length > 0) setActiveTab(allowedTabs[0].name);
  }, [allowedTabs]);

  const scrollTabs = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      if (direction === "left") {
        setIsLeftClicked(true);
        setTimeout(() => setIsLeftClicked(false), 200);
      } else {
        setIsRightClicked(true);
        setTimeout(() => setIsRightClicked(false), 200);
      }
    }
  };

  if (loader) return <Loader />;

  // if (allowedTabs.length === 0) {
  //   return (
  //     <div className="text-center text-gray-600 mt-10">
  //       You do not have permission to update any section of this Micro Grant.
  //     </div>
  //   );
  // }

  const currentTabPermission = allTabs.find(
    (t) => t.name === activeTab
  )?.permission;
  const allowUpdate = permissions.includes(currentTabPermission);

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Micro Grant
        </h1>
        <BackButton text="Back" to="/micro-grant" />
      </div>
      <div className="mt-6">
        <div className="flex items-center rounded-t-lg bg-[#fdf5ec] relative">
          <button
            onClick={() => scrollTabs("left")}
            className={`text-xl px-4 py-2 font-bold rounded-lg transition-colors ${
              isLeftClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            «
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-hidden scrollbar-hide"
          >
            {allowedTabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`px-6 py-2 font-semibold whitespace-nowrap ${
                  activeTab === tab.name
                    ? "bg-[#caa316] text-white"
                    : "text-black"
                }`}
                style={{
                  borderTopLeftRadius: "13px",
                  borderTopRightRadius: "30px",
                }}
              >
                {tab.name}
              </button>
            ))}
          </div>

          <button
            onClick={() => scrollTabs("right")}
            className={`ml-auto text-xl px-4 py-2 rounded-lg font-bold transition-colors ${
              isRightClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            »
          </button>
        </div>
        <div className="bg-white rounded-b-lg shadow-md p-6">
          {activeTab === "Administrative Info" && (
            <AdministrativeInfo
              grantId={id}
              grantData={grantData}
              onGrantUpdated={fetchGrantData}
              isEditMode={true}
              allowUpdate={allowUpdate}
            />
          )}

          {activeTab === "Beneficiary Details" && (
            <BeneficiaryDetails
              grantId={id}
              initialData={grantData}
              onGrantUpdated={fetchGrantData}
              isEditMode={true}
              allowUpdate={allowUpdate}
            />
          )}

          {activeTab === "Micro Grant Details" && (
            <MicroGrantDetails
              grantId={id}
              initialData={grantData}
              onGrantUpdated={fetchGrantData}
              isEditMode={true}
              allowUpdate={allowUpdate}
            />
          )}

          {activeTab === "Verification & Support" && (
            <VerificationSupport
              grantId={id}
              initialData={grantData}
              onGrantUpdated={fetchGrantData}
              isEditMode={true}
              allowUpdate={allowUpdate}
            />
          )}

          {activeTab === "Overall Progress" && (
            <OverallProgress
              grantId={id}
              initialData={grantData}
              onGrantUpdated={fetchGrantData}
              isEditMode={true}
              allowUpdate={allowUpdate}
            />
          )}

          {activeTab === "Attachments" && (
            <Attachments
              grantId={id}
              isEditMode={true}
              onGrantUpdated={fetchGrantData}
              allowUpdate={allowUpdate}
            />
          )}
        </div>
      </div>  
    </div>
  );
};

export default EditMicroGrant;